import { Checkbox, Form, Input, Spin } from "antd";
import { ReactElement, useEffect, useLayoutEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  maxLengthBik,
  maxLengthCheck,
  maxLengthCorrespondentCheck,
} from "../../../../constants/maxLengthInFolders";
import { ruleMaxAccount } from "../../../../constants/rules/RulesConstantsApplication";
import {
  ruleBankName,
  rulePhoneLength,
  ruleRequired,
} from "../../../../constants/rules/RulesConstantsForms";
import { ruleAddress } from "../../../../constants/rules/RulesConstantsUser";
import { IAdditionalInformationProps } from "../../../../interfaces/props/IAdditionalInformationProps";
import { useGetApplicationDataToBankQuery } from "../../../../store/api/applicationBankApi/applicationBank.api";
import { useAppDispatch } from "../../../../store/reduxHooks";
import { SET_APPLICATION_DATA_TO_BANK } from "../../../../store/slice/applicationDataToBankSlice/applicationDataToBankSlice";
import { UI_ITONumericInput } from "../../../../UI_ITO/UI_ITONumericInput/UI_ITONumericInput";
import { UI_ITOPhoneInput } from "../../../../UI_ITO/UI_ITOPhoneInput/UI_ITOPhoneInput";
import { UI_ITOSelectAddressInput } from "../../../../UI_ITO/UI_ITOSelectAddressInput/UI_ITOSelectAddressInput";
import { validateBik } from "../../../../utils/utilities/validateDataToBank";

import classes from "./AdditionalInformation.module.css";

/**
 * Компонент для отображения и редактирования дополнительной информации заявки.
 *
 * @param {IAdditionalInformationProps} props - Свойства компонента.
 * @param {Form} props.form - Форма Ant Design для управления состоянием формы.
 */
export function AdditionalInformation(
  props: IAdditionalInformationProps,
): ReactElement {
  const { form } = props;
  const { applicationId, applicationBankId } = useParams();
  const dispatch = useAppDispatch();

  // Данные дополнительной заявки
  const {
    data: applicationAdditional,
    isError,
    isLoading,
  } = useGetApplicationDataToBankQuery({
    applicationId: Number(applicationId),
    applicationBankId: Number(applicationBankId),
  });

  useEffect(() => {
    if (applicationAdditional) {
      dispatch(SET_APPLICATION_DATA_TO_BANK(applicationAdditional));
    }
  });

  const initialValues = {
    contactApplication: applicationAdditional?.contact_application,
    bankRequisites: applicationAdditional?.bank_requisites,
  };

  const [legalMatchesActual, setLegalMatchesActual] = useState<boolean>(false);

  // Подставляем initialValues в форму, когда появляются результаты выполнения запроса,
  // чтобы на фоне лоадера отображался контент формы
  useLayoutEffect(() => {
    if (applicationAdditional) {
      form.resetFields();
      setLegalMatchesActual(
        applicationAdditional?.contact_application
          ?.actual_address_organization || false,
      );
    }
  }, [
    applicationAdditional?.contact_application,
    applicationAdditional?.bank_requisites,
    form,
  ]);

  function toggleAddressMatch() {
    return setLegalMatchesActual(!legalMatchesActual);
  }

  if (isError) return <></>;

  return (
    <Spin spinning={isLoading} size="large">
      <Form
        className={classes.form}
        name="additionalInformationForm"
        form={form}
        layout="vertical"
        autoComplete="off"
        initialValues={initialValues}
        scrollToFirstError={{ behavior: "smooth", block: "center" }}
      >
        <div
          className={classes.form__fieldset}
          role="group"
          aria-describedby="additionalInformation"
        >
          <div id="additionalInformation" className={classes.form__legend}>
            Дополнительная информация
          </div>
          <Form.Item
            name={["contactApplication", "address_legal"]}
            label="Юридический адрес организации"
            rules={[ruleRequired, ruleAddress]}
            is-full-width="true"
          >
            <UI_ITOSelectAddressInput size="large" />
          </Form.Item>
          <Form.Item
            valuePropName="checked"
            name={["contactApplication", "actual_address_organization"]}
            is-full-width="true"
          >
            <Checkbox
              onChange={toggleAddressMatch}
              className={classes.form__checkBox}
            >
              Юридический адрес совпадает с фактическим
            </Checkbox>
          </Form.Item>
          {!legalMatchesActual && (
            <Form.Item
              name={["contactApplication", "address_factual"]}
              label="Фактический адрес организации"
              rules={[ruleRequired, ruleAddress]}
              is-full-width="true"
            >
              <Input placeholder="Введите фактический адрес" size="large" />
            </Form.Item>
          )}
          <Form.Item
            name={["contactApplication", "post_address_organization"]}
            label="Почтовый адрес"
            rules={[ruleRequired, ruleAddress]}
            is-full-width="true"
          >
            <Input placeholder="Введите почтовый адрес" size="large" />
          </Form.Item>
          <Form.Item
            label="Контактный телефон клиента"
            rules={[rulePhoneLength, ruleRequired]}
            name={["contactApplication", "phone"]}
          >
            <UI_ITOPhoneInput
              placeholder="Введите телефон клиента"
              size="large"
            />
          </Form.Item>
          <Form.Item
            label="Контактный Email"
            name={["contactApplication", "email"]}
            rules={[
              {
                type: "email",
                message: "Введите корректный email",
              },
              ruleRequired,
            ]}
          >
            <Input
              className={classes.form__emailInput}
              placeholder="Введите контактный email"
              size="large"
            />
          </Form.Item>
        </div>
        <div
          className={classes.form__fieldset}
          role="group"
          aria-describedby="bankRequisites"
        >
          <div id="bankRequisites" className={classes.form__legend}>
            Реквизиты
          </div>
          <Form.Item
            name={["bankRequisites", "bik"]}
            label="БИК"
            rules={[ruleRequired, { validator: validateBik }]}
          >
            <UI_ITONumericInput
              placeholder="Введите БИК"
              maxLength={maxLengthBik}
              size="large"
            />
          </Form.Item>
          <Form.Item
            name={["bankRequisites", "name_bank"]}
            label="Наименование банка"
            rules={[ruleRequired, ruleBankName]}
          >
            <Input placeholder="Введите наименование банка" size="large" />
          </Form.Item>
          <Form.Item
            name={["bankRequisites", "account_number"]}
            label="Номер счета"
            rules={[ruleRequired, ruleMaxAccount]}
          >
            <UI_ITONumericInput
              style={{ width: "100%" }}
              placeholder="Введите номер счета"
              maxLength={maxLengthCheck}
              size="large"
            />
          </Form.Item>
          <Form.Item
            name={["bankRequisites", "account_correspondent"]}
            label="Корреспондентский счет"
            rules={[ruleRequired, ruleMaxAccount]}
          >
            <UI_ITONumericInput
              style={{ width: "100%" }}
              placeholder="Введите корреспондентский счет"
              maxLength={maxLengthCorrespondentCheck}
              size="large"
            />
          </Form.Item>
        </div>
      </Form>
    </Spin>
  );
}
